import { css, type Theme } from '@emotion/react';
import { transparentize } from 'polished';
import type { ComponentWithValues } from 'common/stack/ComponentWithValuesDocuments';
import type { OnLog } from 'client/state/LogState';
import { Stack } from 'client/components/Stack';
import { applyEffect } from 'client/Theme';
import { ComponentConnectionStrings } from './ComponentConnectionStrings';
import { ComponentContainersStatus } from './ComponentContainersStatus';
import { ComponentHosts } from './ComponentHosts';
import type { ComponentStatusTree } from './componentsStatusUtils';
import { ComponentStatusHeader } from './ComponentStatusHeader';
import { ComponentStoragesStatus } from './ComponentStoragesStatus';

interface ComponentStatusProps {
  deploymentID: number | null | undefined;
  deploymentUrl: string;
  componentStatusTree: ComponentStatusTree;
  component: ComponentWithValues | null | undefined;
  onLog: OnLog;
}

export const ComponentStatus = ({ deploymentID, deploymentUrl, componentStatusTree, component, onLog }: ComponentStatusProps) => {
  return (
    <Stack component="article" gap={16} css={containerStyle}>
      <ComponentStatusHeader
        deploymentID={deploymentID}
        deploymentUrl={deploymentUrl}
        componentStatusTree={componentStatusTree}
        component={component}
        onLog={onLog}
      />

      {/* Hosts */}
      <ComponentHosts component={component} css={sectionStyle} />

      {/* Connection strings */}
      <ComponentConnectionStrings component={component} css={sectionStyle} />

      {/* Storage */}
      <ComponentStoragesStatus items={componentStatusTree.storage} css={sectionStyle} />

      {/* Containers */}
      <ComponentContainersStatus
        deploymentID={deploymentID}
        deploymentUrl={deploymentUrl}
        items={componentStatusTree.containers}
        componentIdentifier={component?.identifier || ''}
        onLog={onLog}
        css={sectionStyle}
      />
    </Stack>
  );
};

const containerStyle = (theme: Theme) => css`
  position: relative;
  padding: 28px 24px;
  border: 2px solid ${theme.color.grey[60]};
  border-radius: ${theme.borderRadius}px;

  &::before {
    content: '';
    position: absolute;
    display: block;
    width: 4px;
    height: calc(100% - (28px * 2));
    background-color: ${theme.color.grey[60]};
    border-radius: 16px;
    left: calc(24px + 10px);

    mask-image: linear-gradient(to bottom, transparent calc(24px + 8px), white calc(24px + 8px + 32px));
  }
`;

const headerStyle = css`
  .body1 {
    font-weight: bold;
  }
`;

const iconStyle = css`
  position: relative;
  display: flex;
  align-self: flex-start;
  margin: 0;

  > span {
    position: absolute;
    right: -6px;
    bottom: -6px;
  }
`;

const titleStyle = css`
  flex: 1;
  align-items: center;
  justify-content: space-between;
`;

const sectionStyle = css`
  padding-left: calc(24px + 16px);

  > .body1 {
    margin-bottom: 4px;
    font-weight: bold;
  }

  > ul {
    margin: 0;
    padding: 0;
  }
`;

const buttonStyle = (theme: Theme) => css`
  background-color: ${transparentize(0.85, theme.color.grey[100])};

  &:hover {
    background-color: ${applyEffect(theme.color.grey[80], 'hover')};
  }
`;
